import React, { useState } from 'react'
import classNames from 'classnames'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { usePage, useSession, useToast } from '~/hooks'
import { Client } from '~/customClients/client'
import { EmailValidateSchema } from '~/validation-schemas'
import { Button } from '../Button'
import { Form } from '../Form'
import Input from '../Input/Input'
import Typography, { TypographyTag, TypographyVariant } from '../Typography'
import styles from './AuthenticationDrawer.module.scss'
import {
  AuthenticationDrawerContentProps,
  AuthenticationDrawersTypes,
} from './AuthenticationDrawer.types'

const ForgotPasswordAuthenticationDrawer = ({
  onClose,
}: AuthenticationDrawerContentProps) => {
  const [passwordResetEmailSent, setPasswordResetEmailSent] = useState(false)
  const router = useRouter()
  const { session } = useSession()
  const { faqLink, contactUsLink } = usePage()
  const { showNotification, closeNotification } = useToast()

  const handleErrorNotification = (message?: string) => {
    showNotification({
      id: 'forgot-password-failed',
      children: message || 'Failed to send email, please try again.',
      autoClose: true,
      type: 'error',
      onClose: closeNotification,
    })
  }

  const handleResetEmail = async (data: { email: string }) => {
    try {
      const result = await Client.auth.forgotPassword(data, session)
      let message = ''
      if (!result.getErrorSafe()) {
        message = result.getValueSafe()
      }

      if (message === 'Email sent') {
        setPasswordResetEmailSent(true)
      } else {
        handleErrorNotification(message)
      }
    } catch (err) {
      handleErrorNotification()
    }
  }

  const renderContent = (register, errors) => {
    return (
      <div className='flex-1 px-4 py-12 md:py-16'>
        {passwordResetEmailSent ? (
          <div className='flex flex-col gap-4'>
            <Typography
              variant={TypographyVariant.Heading5}
              tag={TypographyTag.p}
              className='text-text-primary'>
              Email sent
            </Typography>
            <Typography
              variant={TypographyVariant.BodyRegular}
              tag={TypographyTag.p}>
              <span className='text-grey-900 leading-6'>
                Instructions on how to reset your password will be sent to the
                nominated email.
              </span>
            </Typography>
            {faqLink?.url && contactUsLink?.url && (
              <Typography
                variant={TypographyVariant.BodyRegular}
                tag={TypographyTag.p}>
                <span className='text-grey-900 leading-6'>
                  For additional troubleshooting, read our{' '}
                  <Link
                    href={faqLink.url}
                    className='underline'
                    target='_blank'>
                    FAQ&apos;s
                  </Link>{' '}
                  or{' '}
                  <Link
                    href={contactUsLink.url}
                    className='underline'
                    target='_blank'>
                    Contact us
                  </Link>
                </span>
              </Typography>
            )}
          </div>
        ) : (
          <>
            <div className='flex flex-col gap-4 mb-6'>
              <Typography
                variant={TypographyVariant.Heading5}
                tag={TypographyTag.p}>
                Forgot password?
              </Typography>
              <Typography
                variant={TypographyVariant.BodyRegularExtraLineHeight}
                tag={TypographyTag.p}>
                Please enter your account email address. Instructions on how to
                reset your password will be sent to the nominated email.
              </Typography>
            </div>
            <div className='pt-8'>
              <Input
                label='Email Address'
                required
                type='text'
                className={styles.input}
                placeholder='Enter your email address'
                name='email'
                register={register}
                error={errors.email?.message.toString()}
              />
            </div>
          </>
        )}
      </div>
    )
  }

  const renderFooter = () => {
    return (
      <div
        className={classNames(
          styles.footerBottom,
          'flex items-center gap-x-4 w-full border-t'
        )}>
        {passwordResetEmailSent ? (
          <Button
            status='primary'
            fluid
            size='lg'
            theme={{ root: styles.footerButton }}
            onClick={() => {
              return onClose(false)
            }}>
            Continue to Jaycar
          </Button>
        ) : (
          <>
            <Button
              status='secondary'
              size='lg'
              fluid
              theme={{ root: styles.footerButton }}
              onClick={() => {
                router.replace(
                  `#${AuthenticationDrawersTypes.Login}`,
                  undefined,
                  {
                    shallow: true,
                  }
                )
              }}>
              Back
            </Button>
            <Button
              htmlType='submit'
              status='primary'
              fluid
              size='lg'
              theme={{ root: styles.footerButton }}>
              Send Email
            </Button>
          </>
        )}
      </div>
    )
  }
  return (
    <Form
      className={styles.formWrapper}
      validationSchema={EmailValidateSchema}
      useFormProps={{ mode: 'onChange' }}
      onSubmit={(data) => {
        handleResetEmail(data)
      }}>
      {({ register, formState: { errors } }) => {
        return (
          <>
            {renderContent(register, errors)}
            {renderFooter()}
          </>
        )
      }}
    </Form>
  )
}

export default ForgotPasswordAuthenticationDrawer
